// src/pages/Help/index.js
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Input,
  InputGroup,
  InputGroupText,
  Button,
} from "reactstrap";

// Components
import HelpCard from "./components/HelpCard";
import QuickStartSection from "./components/QuickStartSection";
import FAQSection from "./components/FAQSection";

// General Help data
import { generalHelpSections } from "./data/generalHelpData";

// Plot Help data
import { helpSections, quickStartData } from "./data/helpData";

const Help = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  // Filter plots based on search query
  const filteredplots = useMemo(() => {
    if (!searchQuery.trim()) {
      return helpSections.plots;
    }
    
    const query = searchQuery.toLowerCase().trim();

    const filterplotList = (plots) => {
      return plots.filter((plot) => {
        // Search in title and description
        const titleMatch = plot.title?.toLowerCase().includes(query);
        const descriptionMatch = plot.description
          ?.toLowerCase()
          .includes(query);

        // If it's a group with subPages, also search in subPages
        if (plot.isGroup && plot.subPages) {
          const subPageMatch = plot.subPages.some(
            (subPage) =>
              subPage.title?.toLowerCase().includes(query) ||
              subPage.description?.toLowerCase().includes(query)
          );
          return titleMatch || descriptionMatch || subPageMatch;
        }
        return titleMatch || descriptionMatch;
      });
    };

    return filterplotList(helpSections.plots);
  }, [searchQuery]);

  const handleplotClick = (helpItem) => {
    if (helpItem.id != "quickstart") {
      navigate(`/help/plot/${helpItem.id}`);
    } else {
      navigate(`/help/${helpItem.id}`);
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery("");
  };

  // Highlight search terms in text
  const highlightSearchTerm = (text, searchTerm) => {
    if (!searchTerm.trim() || !text) return text;

    const regex = new RegExp(
      `(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi"
    );
    return text.replace(
      regex,
      '<mark style="background-color: #fef3c7; color: #92400e; padding: 0 2px; border-radius: 2px;">$1</mark>'
    );
  };

  document.title = "Help | EIRNABio Connect";

  return (
    <div className="page-content">
      <Container fluid>
        {/* Header */}
        <Row className="mb-3">
          <Col lg={12}>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-0 fw-bold" style={{ color: "#374151" }}>
                  Help & FAQ
                </h4>
              </div>
            </div>
          </Col>
        </Row>

        {/* Quick Start and FAQ Sections */}
        <Row className="mb-2">

          {/* FAQ Section */}
          <Col lg={12} md={12}>
            <FAQSection faqs={helpSections.faqs} />
          </Col>
        </Row>

        {/* General Help Section */}
        <Row>
          <Col lg={12}>
            <Card
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: "4px",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                backgroundColor: "#ffffff",
              }}
            >
              <CardBody style={{ padding: "24px" }}>
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                  <h5 className="mb-0 fw-bold" style={{ color: "#374151" }}>
                    General help pages
                  </h5>
                </div>

                {/* plots Grid */}
                <Row>

                  {generalHelpSections.helpSectionArray.map((section, index) => (
                    <Col lg={3} md={4} key={section.id} className="mb-4">
                      <HelpCard
                        helpItem={{
                          ...section,
                        }}
                        onplotClick={handleplotClick}
                        type="plot"
                      />
                    </Col>
                  ))}
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Help plot Details Section */}
        <Row>
          <Col lg={12}>
            <Card
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: "4px",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                backgroundColor: "#ffffff",
              }}
            >
              <CardBody style={{ padding: "24px" }}>
                {/* Header with Search */}
                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                  <h5 className="mb-0 fw-bold" style={{ color: "#374151" }}>
                    Plot specific help pages
                    {searchQuery && (
                      <span
                        className="text-muted ms-2"
                        style={{ fontSize: "14px", fontWeight: "normal" }}
                      >
                        ({filteredplots.length} result
                        {filteredplots.length !== 1 ? "s" : ""} for "
                        {searchQuery}")
                      </span>
                    )}
                  </h5>

                  {/* Search Box */}
                  <div style={{ minWidth: "300px", maxWidth: "400px" }}>
                    <InputGroup
                      style={{
                        borderRadius: "8px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                      }}
                    >
                      <InputGroupText
                        style={{
                          backgroundColor: "#f8f9fa",
                          border: "1px solid #e5e7eb",
                          borderRight: "none",
                        }}
                      >
                        <i className="ri-search-line text-muted" />
                      </InputGroupText>
                      <Input
                        type="text"
                        placeholder="Search help plots..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                          border: "1px solid #e5e7eb",
                          borderLeft: "none",
                          fontSize: "14px",
                          padding: "8px 12px",
                        }}
                      />
                      {searchQuery && (
                        <InputGroupText
                          style={{
                            backgroundColor: "#f8f9fa",
                            border: "1px solid #e5e7eb",
                            borderLeft: "none",
                            cursor: "pointer",
                          }}
                          onClick={clearSearch}
                        >
                          <i className="ri-close-line text-muted" />
                        </InputGroupText>
                      )}
                    </InputGroup>
                  </div>
                </div>

                {/* plots Grid */}
                {filteredplots.length > 0 ? (
                  <Row>
                    {filteredplots.map((plot, index) => (
                      <Col lg={3} md={4} key={plot.id} className="mb-4">
                        <HelpCard
                          helpItem={{
                            ...plot,
                            // Highlight search terms in title and description
                            title: highlightSearchTerm(
                              plot.title,
                              searchQuery
                            ),
                            description: highlightSearchTerm(
                              plot.description,
                              searchQuery
                            ),
                          }}
                          onplotClick={handleplotClick}
                          type="plot"
                          searchQuery={searchQuery} // Pass search query for highlighting
                        />
                      </Col>
                    ))}
                  </Row>
                ) : (
                  // No search results
                  <div className="text-center py-5">
                    <div
                      style={{
                        padding: "60px 40px",
                        backgroundColor: "#f8f9fa",
                        borderRadius: "12px",
                        maxWidth: "500px",
                        margin: "0 auto",
                      }}
                    >
                      <div
                        style={{
                          width: "80px",
                          height: "80px",
                          backgroundColor: "#e5e7eb",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0 auto 24px",
                        }}
                      >
                        <i
                          className="ri-search-line"
                          style={{ fontSize: "36px", color: "#9ca3af" }}
                        />
                      </div>
                      <h5
                        style={{
                          color: "#374151",
                          fontWeight: "600",
                          marginBottom: "12px",
                        }}
                      >
                        No plots found
                      </h5>
                      <p
                        className="text-muted mb-4"
                        style={{ fontSize: "16px" }}
                      >
                        No help plots match your search for{" "}
                        <strong>"{searchQuery}"</strong>
                      </p>
                      <Button
                        color="outline-secondary"
                        size="sm"
                        onClick={clearSearch}
                      >
                        <i className="ri-close-line me-1" />
                        Clear search
                      </Button>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Help;
